import {Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ElementRef} from "@angular/core";
import {Observable, Subscription} from "rxjs";
import * as lodash from "lodash";
import {
    ITCIMarketplaceListing,
    TCI_MARKETPLACE_LISTING_VIEW_MODE,
    TCITenantAppType,
    TCI_ENVIRONMENT_VAR_TYPE,
    ITCIEnvVars,
    TCI_MARKETPLACE_LISTING_TYPE
} from "@tci/common";
import {SelectItem} from "primeng/primeng";
import {FormControl} from "@angular/forms";
import {NgToolsService} from "../ng-shared/ng-util/ng-tools.service";
import {NgEnvVarsService} from "../ng-shared/ng-services/ng-env-vars.service";
import {NgMarketplaceV2Service} from "./ng-marketplace-v2.service";

const MK_MINI_FILTER_FIELD = {
    DISPLAY_NAME: {label: "All", value: "displayName"},
};

@Component({
    selector: "ng-marketplace-v2-mini",
    templateUrl: "./ng-marketplace-v2-mini.component.html",
    styleUrls: ["./ng-marketplace-v2-mini.component.less"]
})
export class NgMarketplaceV2MiniComponent implements OnInit, OnDestroy {
    @Input() tenantAppType: TCITenantAppType;
    @Input() mkTags: string[];
    @Input() miniMKListingType: TCI_MARKETPLACE_LISTING_TYPE = TCI_MARKETPLACE_LISTING_TYPE.Application;
    @Output() output: EventEmitter<ITCIMarketplaceListing> = new EventEmitter();
    @ViewChild("miniCardScrollEle") miniCardScrollEle: ElementRef;

    private subscriptions: Subscription[] = [];

    // For the cards listing
    listingsData: ITCIMarketplaceListing[] = [];
    filteredListingsData: ITCIMarketplaceListing[] = [];
    listingMode = TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD;
    isLoading = true;

    // For highlight
    selectedListing: ITCIMarketplaceListing;
    isMkLeftArrowDisabled = true;
    isMkRightArrowDisabled = false;
    maxMKListInView = 3;

    TAG_NAME: string;
    requestConnectorUrl = "";

    TAG_OPTIONS: SelectItem[] = [
        MK_MINI_FILTER_FIELD.DISPLAY_NAME,
    ];
    nameFilterCtrl = new FormControl();

    constructor(private mkService: NgMarketplaceV2Service,
                private toolsSvc: NgToolsService,
                private envVarService: NgEnvVarsService) {
    }

    ngOnInit(): void {
        // if no marketplace tags defined in TCITenantAppTypeMetaData for left side menu category, should keep tag list to be empty. IPAS-19754
        if (this.mkTags && this.mkTags.length) {
            this.mkTags.forEach(option => {
                const tag: SelectItem = {
                    label: option,
                    value: option
                };
                this.TAG_OPTIONS.push(tag);
            });
        }
        this.subscriptions.push(
            this.getRequestConnectorUrl(),
            this.mkService.getRefreshMiniListSubject()
                .take(1)
                .subscribe(res => {
                    if (res) {
                        this.initData();
                    }
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    createMarketplaceApp($event: ITCIMarketplaceListing) {
        this.output.emit($event);
    }

    goToMarketplace() {
        // close create dialog and redirect page to Marketplace
        this.output.emit(null);
    }

    selectListing(asset: ITCIMarketplaceListing) {
        this.selectedListing = asset;
        this.mkService.selectListing(asset);
    }

    private getRequestConnectorUrl() {
        return this
            .envVarService
            .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_SCRIBE_REQUEST_CONNECTOR_URL)
            .subscribe(
                (envVars: ITCIEnvVars<string>) => {
                    this.requestConnectorUrl = envVars[TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_SCRIBE_REQUEST_CONNECTOR_URL];
                }
            );
    }

    private initData() {
        this.selectedListing = null;
        this.mkService.selectListing(null);
        this.nameFilterCtrl.setValue("");
        this.TAG_NAME = MK_MINI_FILTER_FIELD.DISPLAY_NAME.value;
        this.initMarketplaceListing();

        this.subscriptions.push(
            this.nameFilterCtrl.valueChanges
                .debounceTime(300)
                .subscribe(() => {
                    this.filterMarketplaceListingData();
                })
        );
    }

    private initMarketplaceListing() {
        this.subscriptions.push(this.mkService.getAllListings()
            .catch(e => {
                console.error("Marketplace error getting listings", e);
                return Observable.of(null);
            })
            // Note: show application marketplace only and when no mkTags array the list should be empty
            .map(items => this.mkTags.length ? items.filter(item => {
                return item.listingType.toUpperCase() === this.miniMKListingType.toUpperCase() &&
                    item.tenantType.equals(this.tenantAppType);
            }) : null)
            .subscribe(data => {
                if (data !== null) {
                    this.listingsData = data;
                    this.filterMarketplaceListingData();
                }
                this.isLoading = false;
            }));
    }

    private alphaNumSort(comparison1: string, comparison2: string) {
        return comparison1.localeCompare(comparison2, "en", {numeric: true, caseFirst: "upper"});
    }

    private filterMarketplaceListingData() {
        let listingData = lodash.cloneDeep(this.listingsData), index = -1;

        listingData = this.filterBySearchName(listingData);
        listingData = this.filterByTag(listingData);

        this.filteredListingsData = listingData;

        if (this.selectedListing) {
            if (!lodash.some(this.filteredListingsData, this.selectedListing)) {
                this.selectedListing = null;
                this.mkService.selectListing(null);
            } else {
                index = lodash.findIndex(this.filteredListingsData, this.selectedListing);
            }
        }
        // Note: wait for dom has been rendered, miniCardScrollEle is available
        this.toolsSvc.setTimeout(() => {
            if (this.miniCardScrollEle) {
                if (index > -1) {
                    // Note: scroll card into the view
                    this.miniCardScrollEle.nativeElement.getElementsByClassName("ng-mk-listing-card")[index].scrollIntoView();
                }
                this.checkScrollButton(this.miniCardScrollEle.nativeElement);
            }
        }, 0);
    }

    filterByTag(listingData: ITCIMarketplaceListing[]): ITCIMarketplaceListing[] {
        let arr: ITCIMarketplaceListing[];
        arr = listingData.sort((a, b) => this.alphaNumSort(a.displayName.toString().trim(), b.displayName.toString().trim()));
        if (this.TAG_NAME === MK_MINI_FILTER_FIELD.DISPLAY_NAME.value && this.mkTags.length) {
            arr = listingData.filter(item => item.tags.filter(tag => this.mkTags.includes(tag.name)).length > 0);
        } else {
            arr = listingData.filter(item => item.tags.filter(tag => tag.name === this.TAG_NAME).length > 0);
        }
        return arr;
    }

    private filterBySearchName(listingData: ITCIMarketplaceListing[]): ITCIMarketplaceListing[] {
        const name = this.getSearchMKInputText();
        if (name) {
            listingData = listingData
                .filter(item => item.displayName.toLowerCase().trim().indexOf(name.toLowerCase().trim()) >= 0 ||
                    item.description.toLowerCase().trim().indexOf(name.toLowerCase().trim()) >= 0);
        }
        return listingData;
    }

    private getSearchMKInputText(): string {
        return this.nameFilterCtrl.value ? this.nameFilterCtrl.value.trim() : "";
    }

    private checkScrollButton($ele) {
        // Note: CSS scroll-behavior: smooth; caused dom scrollLeft number delay, need to calculate later
        // https://stackoverflow.com/questions/58140401/how-to-get-actual-scrollleft-of-a-element-when-using-scroll-behavior-smooth
        this.toolsSvc.setTimeout(() => {
            this.isMkLeftArrowDisabled = Math.ceil($ele.scrollLeft) === 0;
            this.isMkRightArrowDisabled = (Math.ceil($ele.scrollLeft) + $ele.offsetWidth) >= $ele.scrollWidth;
        // Note: scroll smoothly animation need to 1 second for firefox browser, attribute scrollLeft of element will be available after 1 second.
        }, 1000);
    }

    scrollMiniMarketplaceCard(isScrollRight: boolean = true) {
        const $ele = this.miniCardScrollEle.nativeElement;
        const style = $ele.firstElementChild.currentStyle || window.getComputedStyle($ele.firstElementChild);
        const cardWidth = $ele.firstElementChild.offsetWidth + parseInt(style.marginLeft, 10) + parseInt(style.marginRight, 10);
        if (isScrollRight) {
            $ele.scrollLeft += cardWidth;
        } else {
            $ele.scrollLeft -= cardWidth;
        }
        this.checkScrollButton($ele);
    }
}
